import { api } from '@/shared/axios-client';
import { useMutation } from '@tanstack/react-query';

// ── Shared response types ───────────────────────────────────────────────────

export interface LinesChangeByStatus {
  executableAdded: number;
  executableModified: number;
  executableDeleted: number;
  added: number;
  modified: number;
  deleted: number;
}

export interface InventoryChangeByStatus {
  added: number;
  modified: number;
  deleted: number;
}

export interface CoverageLinesModeStats {
  coveredChangedLines: number;
  totalChangedLines: number;
  notCoveredLines: number;
  percentage: number;
  locCoveragePercentage: number;
}

export interface CoverageInventoryModeStats {
  coveredChangedElements: number;
  totalChangedElements: number;
  notCoveredElements: number;
  percentage: number;
  inventoryCoveragePercentage: number;
}

export interface EntityBreakdownItem {
  totalChanged: number;
  coveredChanged: number;
  percentage: number;
}

export interface EntityTypeBreakdown {
  methods: EntityBreakdownItem;
  functions: EntityBreakdownItem;
  classes: EntityBreakdownItem;
  branches: EntityBreakdownItem;
  loops: EntityBreakdownItem;
}

export interface CoverageSummary {
  filesChanged: number;
  linesChangeByStatus: LinesChangeByStatus;
  nonExecutableChangeByStatus: {
    nonExecutableAdded: number;
    nonExecutableModified: number;
    nonExecutableDeleted: number;
  };
  inventoryChangeByStatus: InventoryChangeByStatus;
  linesOfCodeMode: CoverageLinesModeStats;
  inventoryMode: CoverageInventoryModeStats;
  entityTypeBreakdown: EntityTypeBreakdown;
}

export interface FileLevelComparisonItem {
  filePath: string;
  fileName?: string;
  status: string;
  linesOfCodeMode?: {
    totalChangedLines: number;
    coveredChangedLines: number;
    notCoveredLines: number;
    percentage: number;
    changedCoveredLineNumbers?: number[];
    notCoveredChangedExecutableLineNumbers?: number[];
  };
  inventoryMode?: {
    totalChangedElements: number;
    coveredChangedElements: number;
    notCoveredElements: number;
    percentage: number;
  };
  addedLineNumbers?: number[];
  deletedLineNumbers?: number[];
  modifiedLineNumbers?: [number, number][];
  totalExecutableLineNumbersInFile?: number[];
  totalNonExecutableLineNumbersInFile?: number[];
  oldContent?: string;
  newContent?: string;
}

export interface ScanInfo {
  scanId: string;
  buildId: number;
  buildNumber: string;
  commitId: string;
  selectedAt: string;
}

export interface CoverageCompareData {
  summary: CoverageSummary;
  fileLevelComparison: FileLevelComparisonItem[];
  scanA: ScanInfo;
  scanB: ScanInfo;
  derivedCoverage?: Record<string, unknown>;
  diffServiceData?: Record<string, unknown>;
}

export interface CoverageCompareResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: CoverageCompareData;
}

// ── Compare by Build (scanId pair) ──────────────────────────────────────────

const TEST_GAP_COMPARE_POLL_MS = 3000;
const TEST_GAP_COMPARE_MAX_WAIT_MS = 10 * 60 * 1000;

export interface CompareBuildRequest {
  scanId1: string;
  scanId2: string;
}

export interface CompareBuildJobAccepted {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
}

export interface CompareBuildJobStatus extends CompareBuildJobAccepted {
  result?: CoverageCompareResponse;
  error?: string;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const compareBuildHeaders = (patToken: string) => ({
  'Requester-Token': patToken,
  'Content-Type': 'application/json',
});

const pollCompareBuildJob = async (
  jobId: string,
  patToken: string,
): Promise<CoverageCompareResponse> => {
  const started = Date.now();
  while (Date.now() - started < TEST_GAP_COMPARE_MAX_WAIT_MS) {
    const job = (await api.get(
      `/api/coverageData/changedCode/compareBuild/jobs/${jobId}`,
      { headers: compareBuildHeaders(patToken) },
    )) as CompareBuildJobStatus;
    if (job.status === 'completed' && job.result) {
      return job.result;
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'Test Gap comparison failed');
    }
    await sleep(TEST_GAP_COMPARE_POLL_MS);
  }
  throw new Error('Test Gap comparison timed out while waiting for the report');
};

const compareCoverageBuildFn = async (
  params: CompareBuildRequest,
  patToken: string,
): Promise<CoverageCompareResponse> => {
  const accepted = (await api.post(
    '/api/coverageData/changedCode/compareBuild',
    params,
    { headers: compareBuildHeaders(patToken) },
  )) as CompareBuildJobAccepted;
  if (!accepted?.jobId) {
    throw new Error('Test Gap compare job was not created');
  }
  return pollCompareBuildJob(accepted.jobId, patToken);
};

export const useCompareCoverageBuild = (patToken: string) => {
  return useMutation<CoverageCompareResponse, Error, CompareBuildRequest>({
    mutationFn: params => compareCoverageBuildFn(params, patToken),
  });
};

// ── Compare by Timeline (date range) ────────────────────────────────────────

export interface CompareTimelineRequest {
  repoId: string;
  date1: string;
  date2: string;
}

const compareCoverageTimelineFn = (
  params: CompareTimelineRequest,
  patToken: string,
): Promise<CoverageCompareResponse> => {
  return api.post('/api/coverageData/changedCode/compareTimeline', params, {
    headers: {
      'Requester-Token': patToken,
      'Content-Type': 'application/json',
    },
  });
};

export const useCompareCoverageTimeline = (patToken: string) => {
  return useMutation<CoverageCompareResponse, Error, CompareTimelineRequest>({
    mutationFn: params => compareCoverageTimelineFn(params, patToken),
  });
};
